import userAuth from '@Config/userauth';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { StrictMode } from 'react';
import {
  Alert,
  Loading,
  Table as TableComponent,
  ViewsModal,
  LabelSection,
  InformationModal
} from "@Components";
import { GridActions } from "@Components/GridComponents";
import { GLOBAL } from "@Config/global";
import { getTableColumns } from '@Shared/Utils/tableColumns';
import { truncate } from '@Shared/Utils';
import PagoLiquidacionesDrillDown from './partials/drillDown';
import { ErrorLevel } from '@Components/Alert/errorLevel';
import { TipoCertificado } from '@Shared/Utils/Models/TipoCertificadoEnum';
import CertificateDownload from '@Components/Table/components/CertificateDownload';
import './styles.scss';
import usePagosYRetenciones from './hooks/usePagosYLiquidaciones';
import pagosYLiquidacionesService from './services/pagosYRetencionesService';
import ConsultaModal from './partials/consultaModal';

const initialState = {
  showViewsModal: false,
  loadingData: false,
  filterTabActive: 3,
  showModal: false,
  modalMsg: '',
  modalStatus: 'success',
  modalTitle: '',
  tableColumns: null,
  searchResultsError: '',
  showResults: false,
  showFilters: false,
  showConsultaModal: false,
  selectedConsultaItem: null
};

const PagosYRetenciones = () => {
  const [state, setState] = useState(initialState);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState([]);
  const [sort, setSort] = useState([]);
  const [poliza, setPoliza] = useState(null);

  const { data, totalItems, loading, error, fetchPagosYRetenciones, downloadCertificado, exportToExcel } = usePagosYRetenciones();

  const showModal = useCallback((status, title, message) => {
    setState(prev => ({
      ...prev,
      showModal: true,
      modalStatus: status,
      modalTitle: title,
      modalMsg: message
    }));
  }, []);

  const handleDownloadCertificado = useCallback(async (item, tipoCertificado) => {
    setState(prev => ({ ...prev, loadingData: true }));

    try {
      const result = await downloadCertificado(item.cuit, item.referencia, tipoCertificado);
      if (result.success) {
        showModal(
          'success',
          'Certificado generado',
          `El certificado se ha descargado correctamente.`
        );
      } else {
        showModal(
          'error',
          'Error de descarga',
          result.error || 'No se pudo generar el certificado.'
        );
      }
    } catch (error) {
      showModal(
        'error',
        'Error de descarga',
        error?.message || 'No se pudo generar el certificado.'
      );
    } finally {
      setState(prev => ({ ...prev, loadingData: false }));
    }
  }, [downloadCertificado, showModal]);

  const ibOsMunicipColumn = useMemo(() => ({
    Header: "IB-OS-Municip",
    accessor: "ibOsMunicip",
    width: 150,
    style: { textAlign: 'right' },
    headerStyle: { textAlign: 'right' },
    filterable: false,
    sortable: false,
    resizable: true,
    Cell: row => {
      const item = row.original;
      const value = item.ibOsMunicip ? `$ ${truncate(item.ibOsMunicip, 2)}` : '';

      return (
        <div style={{ marginLeft: 'auto', width: 'fit-content' }}>
          <CertificateDownload
            value={value}
            onDownload={() => handleDownloadCertificado(item, TipoCertificado.IIBBObraSocialMunicipal)}
            tooltipText="Certificado de Retención de IB-OS-Municip"
            disabled={!item.ibOsMunicip}
          />
        </div>
      );
    }
  }), [handleDownloadCertificado]);

  const gananciasColumn = useMemo(() => ({
    Header: "Ganancias",
    accessor: "ganancias",
    width: 150,
    style: { textAlign: 'right' },
    headerStyle: { textAlign: 'right' },
    filterable: false,
    sortable: false,
    resizable: true,
    Cell: row => {
      const item = row.original;
      const value = item.ganancias > 0 ? `$ ${truncate(item.ganancias, 2)}` : '';

      return (
        <div style={{ marginLeft: 'auto', width: 'fit-content' }}>
          <CertificateDownload
            value={value}
            onDownload={() => handleDownloadCertificado(item, TipoCertificado.Ganancias)}
            tooltipText="Certificado de Retención Ganancias"
            disabled={item.ganancias <= 0}
          />
        </div>
      );
    }
  }), [handleDownloadCertificado]);

  const handleOpenConsultaModal = useCallback((item) => {
    setState(prev => ({
      ...prev,
      showConsultaModal: true,
      selectedConsultaItem: item
    }));
  }, []);

  const handleCloseConsultaModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      showConsultaModal: false,
      selectedConsultaItem: null
    }));
  }, []);

  const handleConsultaSent = useCallback(() => {
    handleCloseConsultaModal();
    showModal(
      'success',
      'Mensaje enviado',
      'Su comentario sera recepcionado y respondido a la brevedad. Muchas gracias.'
    );
  }, [handleCloseConsultaModal, showModal]);

  const consultaReclamoColumn = useMemo(() => ({
    Header: "Consulta",
    accessor: "consulta",
    width: 150,
    style: { textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
    filterable: false,
    sortable: false,
    resizable: true,
    Cell: row => {
      const item = row.original;

      return (
        <button
          type="button"
          className="btn btn-link"
          onClick={() => handleOpenConsultaModal(item)}
        >
          Generar<i className="bi bi-box-arrow-up-right" style={{ margin: 2 }}></i>
        </button>
      );
    }
  }), [handleOpenConsultaModal]);

  const handleSaveViewModal = useCallback((newListColumns) => {
    const { REPORT_FORMAT } = GLOBAL;
    let columns = {
      "fecha": { title: "Fecha de pago", tooltip: 'Fecha de pago', field: 'Fecha', format: REPORT_FORMAT.FECHA, show: true },
      "numeroSolicitudPago": { title: "Referencia", tooltip: 'Número de solicitud de pago', field: 'Referencia', show: true },
      "tipoPago": { title: "Tipo de pago", field: 'TipoPago', format: REPORT_FORMAT.TEXTO, show: true },
      "importePagado": { title: "Importe pagado", field: 'ImportePagado', format: REPORT_FORMAT.MONEDA, show: true },
    };

    const originalColumns = getTableColumns(columns);

    const transformedColumns = newListColumns.map(modalCol => {
      const originalCol = originalColumns.find(origCol => origCol.name === modalCol.name);
      if (originalCol) {
        return {
          ...originalCol,
          show: modalCol.show
        };
      }
      return null;
    }).filter(col => col !== null);

    setState(prev => ({
      ...prev,
      tableColumns: transformedColumns,
      showViewsModal: false
    }));
  }, []);

  const createBaseTableColumns = useCallback(() => {
    if (state.tableColumns) {
      return state.tableColumns;
    }

    const { REPORT_FORMAT } = GLOBAL;
    let newTableColumns = [];
    let columns = {
      "fecha": { title: "Fecha de pago", tooltip: 'Fecha de pago', field: 'Fecha', format: REPORT_FORMAT.FECHA, show: true },
      "numeroSolicitudPago": { title: "Referencia", tooltip: 'Número de solicitud de pago', field: 'Referencia', show: true },
      "tipoPago": { title: "Tipo de pago", field: 'TipoPago', format: REPORT_FORMAT.TEXTO, show: true },
      "importePagado": { title: "Importe pagado", field: 'ImportePagado', format: REPORT_FORMAT.MONEDA, show: true },
    };

    let cols = getTableColumns(columns);
    cols.forEach((c) => { newTableColumns.push(c) });
    return newTableColumns;
  }, [state.tableColumns]);

  const createListTableColumns = useCallback(() => {
    const baseColumns = createBaseTableColumns();
    return [...baseColumns.filter(f => f.show), ibOsMunicipColumn, gananciasColumn, consultaReclamoColumn];
  }, [createBaseTableColumns, ibOsMunicipColumn, gananciasColumn, consultaReclamoColumn]);

  const createExcelReportColumns = useCallback(() => {
    const { REPORT_FORMAT } = GLOBAL;
    let columns = {
      "fecha": { title: "Fecha de pago", tooltip: 'Fecha de pago', field: 'Fecha', format: REPORT_FORMAT.FECHA, show: true, width: 120 },
      "numeroSolicitudPago": { title: "Referencia", tooltip: 'Número de solicitud de pago', field: 'Referencia', show: true, width: 120 },
      "tipoPago": { title: "Tipo de pago", field: 'TipoPago', format: REPORT_FORMAT.TEXTO, show: true, width: 100 },
      "importePagado": { title: "Importe pagado", field: 'ImportePagado', format: REPORT_FORMAT.MONEDA, show: true },
      "ibOsMunicip": { title: "IB-OS-Municipal", field: 'IbOsMunicip', format: REPORT_FORMAT.MONEDA, show: true },
      "ganancias": { title: "Ganancias", field: 'Ganancias', format: REPORT_FORMAT.MONEDA, show: true },
    };

    const baseColumns = getTableColumns(columns);
    return baseColumns.filter(f => f.show).map(c => ({
      Title: c.title,
      Field: c.field,
      Summarize: c.summarize,
      Format: c.format
    }));
  }, []);

  const handleSubmit = useCallback(async (event) => {
    if (event) event.preventDefault();

    setState(prev => ({ ...prev, searchResultsError: '' }));
    try {
      const searchParams = pagosYLiquidacionesService.buildSearchParams(filters, sort, page, pageSize, poliza);
      console.log('Fetching with params:', searchParams);
      await fetchPagosYRetenciones(searchParams);
      setState(prev => ({ ...prev, showResults: true }));
    } catch (error) {
      console.error('Error fetching data:', error);
      setState(prev => ({ ...prev, searchResultsError: error?.message || "Error al cargar los datos" }));
    }
  }, [filters, sort, page, pageSize, poliza, fetchPagosYRetenciones]);

  const handleExportSubmit = useCallback(async (event) => {
    event?.preventDefault();
    if (!userAuth.hasBusinessPermission('settlements.payments.export')) return;

    setState(prev => ({ ...prev, loadingData: true }));
    try {
      const columns = createExcelReportColumns();
      const exportParams = pagosYLiquidacionesService.buildExportParams('', columns);
      await exportToExcel(exportParams);
    } catch (error) {
      setState(prev => ({ ...prev, searchResultsError: "Error al exportar los datos" }));
    } finally {
      setState(prev => ({ ...prev, loadingData: false }));
    }
  }, [createExcelReportColumns, exportToExcel]);

  const applySort = useCallback((event) => {
    if (event.length > 0) {
      setSort(event);
    } else {
      setSort({});
    }
  }, []);

  const applyFilter = useCallback((event) => {
    if (event.length > 0) {
      setPage(0);
      setFilters(event);
    } else {
      setFilters([]);
    }
  }, []);

  const changeSize = useCallback((size) => {
    setPageSize(size);
  }, []);

  const changePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const clearFiltros = useCallback(() => {
    setPage(0);
    setPoliza(null);
    setFilters([]);
    setSort([]);
  }, []);

  const onKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  }, [handleSubmit]);

  // Efectos
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (isFirstLoad.current || state.showResults) {
      handleSubmit();
      isFirstLoad.current = false;
    }
  }, [sort, page, pageSize, filters, poliza, handleSubmit]);

  // Memized values
  const searchTableColumns = useMemo(() => createListTableColumns(), [createListTableColumns]);
  const pages = useMemo(() => Math.ceil(totalItems / pageSize), [totalItems, pageSize]);

  const { TABLE_VIEW } = GLOBAL;
  const { COMMON_SEARCH_FORM } = GLOBAL;

  return (
    <StrictMode>
      {state.showViewsModal && <ViewsModal
        listColumns={createBaseTableColumns()}
        onDismiss={() => { setState(prev => ({ ...prev, showViewsModal: false })) }}
        onSave={handleSaveViewModal}
      />}
      <Loading visible={state.loadingData} />
      {state.showModal && (
        <InformationModal
          title={state.modalTitle}
          icon={state.modalStatus === 'error' ? 'bi bi-exclamation-triangle' : 'bi bi-info-circle'}
          status={state.modalStatus}
          message={state.modalMsg}
          onConfirm={() => setState(prev => ({ ...prev, showModal: false }))}
        />
      )}
      <div className="pagos-liquidaciones-layout">
        <div className="pagos-liquidaciones-header">
          <LabelSection section="pagosYLiquidaciones" type="subtitulo" className="form-title" />
        </div>
        <div className="pagos-liquidaciones-form-container">
          <h1>Pagos y Retenciones
          </h1>
          <form id="pagos_liquidaciones_search_form_1" onSubmit={handleSubmit} onKeyDown={onKeyDown} noValidate>
            <GridActions
                  showExport={userAuth.hasBusinessPermission('settlements.payments.export')}
              title="Pagos y Retenciones"
              onSearch={handleSubmit}
              onClearFilters={clearFiltros}
              onExport={handleExportSubmit}
              onManageColumns={() => setState(prev => ({ ...prev, showViewsModal: true }))}
              onToggleFilters={() => setState(prev => ({ ...prev, showFilters: !prev.showFilters }))}
              showFilters={state.showFilters}
              showResults={state.showResults}
              totalItems={totalItems}
              loading={loading}
              searchButtonText={COMMON_SEARCH_FORM.SEARCH_BUTTON}
              clearFiltersButtonText={COMMON_SEARCH_FORM.CLEAR_FILTERS}
              exportButtonText={TABLE_VIEW.EXPORT_TEXT}
              viewButtonText={COMMON_SEARCH_FORM.VIEW_BUTTON}
              className="pagos-liquidaciones-actions-section"
              hasFilters={false}
            />

            {state.searchResultsError && (
              <div id="form-error-message" className="row">
                <div className="col-12">
                  <Alert
                    icon="times"
                    message=""
                    title={state.searchResultsError}
                    errorInfo={state.searchResultsError}
                    level={ErrorLevel.Error}
                  />
                </div>
              </div>
            )}
            {(state.showResults || loading) && (
              <div className="row">
                <TableComponent
									loading={loading}
                  onKeyDown={onKeyDown}
                  columns={searchTableColumns}
                  sorted={sort}
                  onSortedChange={applySort}
                  filter={filters}
                  data={data}
                  manual={true}
                  onFilterChange={applyFilter}
                  page={page}
                  pageSize={pageSize}
                  onPageSizeChange={changeSize}
                  pages={pages}
                  expandable={true}
                  changePage={changePage}
                  subComponent={props => <PagoLiquidacionesDrillDown {...props} />}
                />
              </div>
            )}
          </form>
        </div>
      </div>
      {state.showConsultaModal && (
        <ConsultaModal
          isOpen={state.showConsultaModal}
          onClose={handleCloseConsultaModal}
          onSubmitSuccess={handleConsultaSent}
          item={state.selectedConsultaItem}
        />
      )}
    </StrictMode>
  );
};

export default PagosYRetenciones;
